import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { faqGroups } from "@/data/faqs";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";

const allFaqs = faqGroups.flatMap((g) => g.items);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function FAQ() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [open, setOpen] = useState({});

  const filteredGroups = useMemo(() => {
    const q = query.toLowerCase().trim();
    return faqGroups
      .filter((g) => activeCat === "all" || g.id === activeCat)
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            !q ||
            it.q.toLowerCase().includes(q) ||
            it.a.toLowerCase().includes(q) ||
            g.label.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [query, activeCat]);

  const totalCount = useMemo(
    () => filteredGroups.reduce((sum, g) => sum + g.items.length, 0),
    [filteredGroups]
  );

  const jumpTo = (id) => {
    setActiveCat("all");
    setTimeout(() => {
      const el = document.getElementById(`faq-${id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 30);
  };

  return (
    <div className="abc-app">
      <Seo
        path="/faq"
        title="Auto Repair FAQ — Birmingham AL"
        description="Answers to common questions about oil changes, tires, brakes, diagnostics, warranty, and fleet care at Auto Basic Care in Birmingham, AL."
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
          faqLd(allFaqs),
        ]}
      />
      <SiteHeader />

      <section className="faq-hero">
        <div className="container faq-hero-inner">
          <div className="badge">Frequently Asked Questions</div>
          <h1>Answers built for trust. Service built for the long haul.</h1>
          <p>
            Search our most-asked questions about oil changes, tires, brakes, diagnostics, warranty
            coverage, fleet care, and more. Still need help? Call us at 205-405-1238.
          </p>

          <div className="faq-search-wrap">
            <input
              type="text"
              placeholder="Search questions… try 'oil', 'brake', 'warranty'"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="faq-search"
            />
            <Link to="/#schedule" className="btn btn-primary" data-testid="faq-book-btn">Book Appointment</Link>
            <a href="tel:2054051238" className="btn btn-outline-light" data-testid="faq-call-btn">Call Now</a>
          </div>

          <div className="trust-row">
            <div className="trust-badge">
              <div className="trust-icon">🛡️</div>
              <div>
                <strong>Warranty Protection</strong>
                <small>12,000-mile workmanship coverage</small>
              </div>
            </div>
            <div className="trust-badge">
              <div className="trust-icon">✓</div>
              <div>
                <strong>Certified Technicians</strong>
                <small>Trusted hands on every repair</small>
              </div>
            </div>
            <div className="trust-badge">
              <div className="trust-icon">⭐</div>
              <div>
                <strong>Preventive Maintenance Experts</strong>
                <small>Keeping you on the road longer</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-body">
        <div className="container">
          <div className="faq-categories" data-testid="faq-categories">
            <button
              className={`faq-cat-pill ${activeCat === "all" ? "active" : ""}`}
              onClick={() => setActiveCat("all")}
              data-testid="faq-cat-all"
            >
              All Topics
            </button>
            {faqGroups.map((g) => (
              <button
                key={g.id}
                className={`faq-cat-pill ${activeCat === g.id ? "active" : ""}`}
                onClick={() => { setActiveCat(g.id); jumpTo(g.id); }}
                data-testid={`faq-cat-${g.id}`}
              >
                <span>{g.icon}</span> {g.label}
              </button>
            ))}
          </div>

          {query && (
            <p className="muted" data-testid="faq-result-count" style={{ marginBottom: 20 }}>
              Showing {totalCount} result{totalCount === 1 ? "" : "s"} for “{query}”
            </p>
          )}

          {filteredGroups.length === 0 ? (
            <div className="info-card" data-testid="faq-no-results">
              <strong>No matches found.</strong>
              <p className="muted">Try a different search term or call us at 205-405-1238 — we&apos;re happy to help.</p>
            </div>
          ) : (
            filteredGroups.map((g) => (
              <div key={g.id} id={`faq-${g.id}`} className="faq-group" data-testid={`faq-group-${g.id}`}>
                <h2><span className="ico" aria-hidden="true">{g.icon}</span> {g.label}</h2>
                <div className="faq-list">
                  {g.items.map((it, i) => {
                    const key = `${g.id}-${i}`;
                    const isOpen = !!open[key];
                    return (
                      <div key={key} className={`faq-item ${isOpen ? "open" : ""}`}>
                        <button
                          className="faq-question"
                          onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                          aria-expanded={isOpen}
                          data-testid={`faq-question-${slug(it.q).slice(0, 40)}`}
                        >
                          <span>{it.q}</span>
                          <span className="plus" aria-hidden="true">{isOpen ? "−" : "+"}</span>
                        </button>
                        <div className="faq-answer">{it.a}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="faq-cta-band">
        <div className="container faq-cta-inner">
          <h2>Ready when you are. Schedule service or request a quote.</h2>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/#schedule" className="btn btn-dark" data-testid="faq-cta-book">Book Appointment</Link>
            <Link to="/tires" className="btn btn-dark" data-testid="faq-cta-quote">Request Tire Quote</Link>
            <a href="tel:2054051238" className="btn btn-dark" data-testid="faq-cta-call">Call 205-405-1238</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
